import React from 'react';

import { EmptyTutorial } from '@dashboard/components/empty-states/EmptyTutorial';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import SimpleTextCell, { SimpleNumberCell } from '@dashboard/components/table/cells/SimpleTextCell';
import { FContactsGroupFragment, useContactsGroupContactsQuery } from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { Link, Paper, Skeleton, Typography } from '@mui/material';

import PageHeader from './PageHeader';
import useCurrentGroups from './useCurrentGroups';

function renderSimpleText(val: string | null | undefined) {
  if (typeof val !== 'string') {
    return '--';
  }
  const v = val.trim();
  if (v.length === 0) {
    return '--';
  }
  return v;
}

function ContactsCountCell({ contactsGroupId }: { contactsGroupId: string }) {
  const { data } = useContactsGroupContactsQuery({ variables: { contactsGroupId } });
  const count = data?.ContactsGroupContacts.count;
  if (count === undefined) {
    return <Skeleton />;
  }
  return <SimpleNumberCell value={count} />;
}

const GroupTable = createTableComponent<FContactsGroupFragment>({
  emptyResults: (
    <EmptyTutorial
      description={
        <>
          <Typography variant="h4" gutterBottom>
            You don't have any unsubscribe groups yet
          </Typography>
          <Typography color="text.secondary" paragraph>
            Unsubscribe groups give your team the option to append an unsubscribe link to a related set of emails.{' '}
            <Link target="_blank" href="https://www.usewaypoint.com/docs/unsubscribe-groups">
              Learn more
            </Link>
            .
          </Typography>
        </>
      }
      videoSource="https://assets.usewaypoint.com/teaser-unsubscribe-groups-voiceover.mp4"
      videoTitle="Video teaser of unsubscribe groups on Waypoint"
    />
  ),
  columns: [
    {
      headerName: 'Name',
      renderCell: ({ row }) => <SimpleTextCell>{renderSimpleText(row.name)}</SimpleTextCell>,
    },
    {
      headerName: 'ID',
      renderCell: ({ row }) => <SimpleTextCell>{renderSimpleText(row.id)}</SimpleTextCell>,
    },
    {
      headerName: 'Key',
      renderCell: ({ row }) => <SimpleTextCell>{renderSimpleText(row.key)}</SimpleTextCell>,
    },
    {
      headerName: 'Contacts',
      renderCell: ({ row }) => <ContactsCountCell contactsGroupId={row.id} />,
    },
  ],
});

export default function GroupsList() {
  const { data } = useCurrentGroups();
  const navigator = usePlatformNavigator();
  const nodes = data?.ContactsGroups.nodes;

  return (
    <Paper>
      <PageHeader />
      <GroupTable
        nodes={nodes}
        onRowClick={(row) => {
          navigator.showContactsGroup(row.id);
        }}
      />
    </Paper>
  );
}
